"use client";

import React from "react";
import { Plus, Leaf, Coffee, Wheat, Info, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
const menuItems = [{
  category: "Core Programs",
  items: [{
    id: "p1",
    title: "Reentry & Reintegration",
    price: "Active",
    description: "Comprehensive support for individuals transitioning back into the community, focusing on restoring stability and dignity through structured guidance.",
    allergens: "Truth, Responsibility",
    sourcing: "Mary E. Jiles and Gregory J. Hand Pathways to Success Center initiative."
  }, {
    id: "p2",
    title: "Case Management",
    price: "Active",
    description: "Individual support planning that navigates community resources and creates personalized roadmaps for long-term transformation.",
    allergens: "Understanding, Skills",
    sourcing: "Community Resource Navigation & Local Partnerships."
  }, {
    id: "p3",
    title: "Mentorship & Development",
    price: "Active",
    description: "Personal development programs and life skills training designed to empower individuals toward independent living readiness.",
    allergens: "Thoughtfulness, Growth",
    sourcing: "Founders Circle Mentorship Network."
  }]
}, {
  category: "Future Initiatives",
  items: [{
    id: "d1",
    title: "Supportive Housing",
    price: "Priority",
    description: "A future program priority focused on providing safe, stable environments that foster healing and belonging.",
    allergens: "Stability, Dignity",
    sourcing: "Strategic planning phase for long-term community impact."
  }, {
    id: "d2",
    title: "Transportation Services",
    price: "Active",
    description: "Reliable transit solutions ensuring participants can access employment, healthcare, and essential community services.",
    allergens: "Opportunity, Access",
    sourcing: "Local Community Partnerships and Volunteer Networks."
  }, {
    id: "d3",
    title: "Pathways Center",
    price: "Vision",
    description: "The signature initiative in development, designed as a hub for empowerment, leadership, and community healing.",
    allergens: "Leadership, Hope",
    sourcing: "Future vision for the Mary E. Jiles and Gregory J. Hand signature facility."
  }]
}];
export default function LaCarteDuMoment() {
  return <section data-section-id="225206" id="comprehensive-support-services" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        <div className="mb-12 md:mb-16 text-center md:text-left">
          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight mb-4 text-primary">
            Comprehensive Support Services
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl font-default">
            Restoring stability, dignity, and opportunity through community-centered programs designed for long-term transformation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          
          {}
          <div className="lg:col-span-5 lg:sticky lg:top-24 h-[400px] lg:h-[calc(100vh-12rem)] min-h-[500px] rounded-3xl overflow-hidden relative shadow-2xl">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654300/img/trust_foundation_mentorship.png" alt="Community mentorship and support" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-primary/20 pointer-events-none" />
            
            {}
            <div className="absolute top-6 left-6 flex flex-col gap-3">
              <Badge className="bg-secondary text-secondary-foreground border-none shadow-lg px-3 py-1.5 text-sm font-medium backdrop-blur-md bg-opacity-90">
                <Leaf className="w-4 h-4 mr-2" />
                501(c)(3) Certified
              </Badge>
              <Badge className="bg-accent text-accent-foreground border-none shadow-lg px-3 py-1.5 text-sm font-medium backdrop-blur-md bg-opacity-90">
                <Coffee className="w-4 h-4 mr-2" />
                Community Led
              </Badge>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <ScrollArea className="h-full lg:h-[calc(100vh-12rem)] lg:pr-6">
              <div className="flex flex-col gap-12 pb-12">
                {menuItems.map((category, catIndex) => <div key={category.category} data-index={catIndex} className="flex flex-col gap-6">
                    <h3 className="font-default text-2xl font-semibold border-b border-border pb-2 text-primary/90">
                      {category.category}
                    </h3>
                    
                    <div className="flex flex-col gap-4">
                      {category.items.map((item, itemIndex) => <div key={item.id} data-index={itemIndex} className="group relative flex items-center justify-between p-2 -mx-2 rounded-2xl hover:bg-muted/50 transition-colors">
                          
                          <div className="flex-1 flex items-baseline justify-between pr-4">
                            <span className="font-serif text-lg md:text-xl font-medium text-foreground">
                              {item.title}
                            </span>
                            
                            {}
                            <div className="flex-1 border-b-2 border-dotted border-muted-foreground/30 mx-4 relative top-[-6px] hidden sm:block" />
                            
                            <span className="font-mono text-base md:text-lg font-semibold text-secondary">
                              {item.price}
                            </span>
                          </div>

                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="rounded-full shrink-0 h-8 w-8 bg-background border border-border shadow-sm hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all">
                                <Plus className="w-4 h-4" />
                                <span className="sr-only">Details for {item.title}</span>
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[425px] rounded-3xl">
                              <DialogHeader>
                                <DialogTitle className="font-serif text-2xl text-primary">{item.title}</DialogTitle>
                                <DialogDescription className="font-mono text-secondary text-lg mt-1">
                                  Status: {item.price}
                                </DialogDescription>
                              </DialogHeader>
                              
                              <div className="mt-4 space-y-6">
                                <div>
                                  <p className="text-foreground font-default leading-relaxed">
                                    {item.description}
                                  </p>
                                </div>
                                
                                <div className="space-y-3 bg-accent/30 p-4 rounded-2xl border border-border/50">
                                  <div className="flex items-start gap-3">
                                    <Wheat className="w-5 h-5 text-primary/70 shrink-0 mt-0.5" />
                                    <div>
                                      <h4 className="font-default text-sm font-semibold text-foreground">Core Values</h4>
                                      <p className="text-sm text-muted-foreground font-default">{item.allergens}</p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-start gap-3">
                                    <MapPin className="w-5 h-5 text-primary/70 shrink-0 mt-0.5" />
                                    <div>
                                      <h4 className="font-default text-sm font-semibold text-foreground">Impact Area</h4>
                                      <p className="text-sm text-muted-foreground font-default">{item.sourcing}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                        </div>)}
                    </div>
                  </div>)}
              </div>
            </ScrollArea>
          </div>

        </div>
      </div>
    </section>;
}